import { useSlotId } from '@react-aria/utils';
import type { RadioProps } from '@react-types/radio';
import clsx from 'clsx';
import * as React from 'react';
import { useRadio, VisuallyHidden } from 'react-aria';

import { useRadioGroupContext } from '../Radio';
import { SegmentedControlContextProvider } from '../SegmentedControlContext';
import { CardProvider } from './Card';
import { useCardButton } from './CardButton';

export type CardRadioProps = RadioProps & {
    className?: string;
    cardHighlight?: string;
};

/**
 * Card radio component to create a radio button with a card layout.
 */
export function CardRadio({ className, ...props }: CardRadioProps) {
    const state = useRadioGroupContext();
    const ref = React.useRef<HTMLInputElement>(null);
    const { inputProps, isSelected, isDisabled } = useRadio(props, state, ref);
    const headingId = useSlotId();
    const descriptionId = useSlotId();
    const inputId = useSlotId();
    const cardProps = useCardButton({
        elementType: 'label',
        disabled: isDisabled,
        headingId,
        descriptionId,
    });
    const ctxValue = React.useMemo(() => ({ isSelected }), [isSelected]);

    return (
        <CardProvider headingId={headingId} descriptionId={descriptionId}>
            <label
                {...cardProps}
                htmlFor={inputId}
                aria-labelledby={headingId}
                aria-describedby={descriptionId}
                className={clsx(
                    className,
                    cardProps.className,
                    'relative transition',
                    isSelected && 'border-neutral-focus',
                    isDisabled && 'cursor-not-allowed',
                    state.isInvalid && 'border-danger-focus'
                )}
            >
                <VisuallyHidden>
                    <input {...inputProps} ref={ref} id={inputId} />
                </VisuallyHidden>
                <SegmentedControlContextProvider value={ctxValue}>
                    {props.children}
                </SegmentedControlContextProvider>
            </label>
        </CardProvider>
    );
}
